import { ThemeComponent } from "@/theme/ThemeComponent";
import { Component, Ref, SimpleEvent, CartItem } from '@eastsideco/salvo-ts';
import { cart } from "@/theme/Cart";
import { formatMoney } from '@/utils/formatting';
import { SideCart } from '../SideCart/SideCart';
import { QuantitySelector } from "../QuantitySelector/QuantitySelector";
@Component
export class CartMain extends ThemeComponent {
    @Ref() summary!: HTMLElement[];
    @Ref({ required: false }) emptyCart!: HTMLElement;
    @Ref() items!: HTMLElement;
    @Ref({ required: false }) count!: HTMLElement[];
    @Ref({ required: false }) totalPrice!: HTMLElement;
    @Ref({ required: false }) lineTotal!: HTMLSpanElement [];
    @Ref({ required: false }) quantitySelector!: QuantitySelector[];

    private itemCount: number = 0;
    private sideCart: SideCart | null = document.body.querySelector('side-cart');

    private _onCartItemCountChange = new SimpleEvent<number>();
    public get onCartItemCountChange() {
        return this._onCartItemCountChange.asEvent();
    }

    /**
	 * Check if variant is in cart.
	 * @param variant - variant id.
	 */
    public checkCart(variant: number) {
        return (cart.items.filter((item) => ~~item.variantId === ~~variant)).length > 0;
    }

    public updateCart(action: string, item: CartItem, quantity?: number, target?: HTMLElement | null) {
        switch (action) {
            case 'add':
                this.handleAddItems(item);
                break;
            case 'remove':
                this.handleRemoveItems(item, target);
                break;
            case 'update':
                this.handleUpdateItem(item, quantity);
                break;
        }
    }

    /**
	 * Hide or show cart items.
	 * @returns
	 */
    private handleContentDisplay() {
        const itemsLength = cart.items.length;

        if (itemsLength <= 0) {
            this.classList.add('is-empty');
            this.classList.remove('has-items');

            for (const summary of this.summary) {
                summary.classList.remove('is-active');
            }

            return;
        }

        this.classList.remove('is-empty');
        this.classList.add('has-items');
        this.itemCount = 0;

        for (const summary of this.summary) {
            summary.classList.add('is-active');
        }
    }

    /**
	 * Handle render of added items
	 */
    private handleAddItems(item: CartItem) {
        fetch(`/products/${item?.handle}/?variant=${item?.variantId}&section_id=cart_item`)
            .then((response) => response.text())
            .then((text) => {
                const cartItemHTML = new DOMParser()
                    .parseFromString(text, 'text/html')
                    .querySelector('.shopify-section');

                if (!cartItemHTML) { return; }

                this.items.prepend(cartItemHTML);
                this.updateCartData();

                if (!this.sideCart) { return; }
                this.sideCart.openCart();
            })
            .catch((event: unknown | null) => {
                if (event instanceof Error) {
                    this.$log.error(event.message);
                }
            });
    }

    /**
	 * Handle render of removed items
	 */
    private handleRemoveItems(item: CartItem, target: HTMLElement | undefined | null) {
        if (!target) { return; }

        try {
            cart.removeItem(item).then(() => {
                const itemRow = target.closest('[data-ref="cartItemRow"]');
                itemRow?.remove();
                this.updateCartData();

            }).catch((event: unknown) => {
                if (event instanceof Error) {
                    this.$log.error(event.message);
                }
            });

        } catch (event: unknown) {
            if (event instanceof Error) {
                this.$log.info('Remove error', event.message);
            }
        }
    }

    /**
	 * Update cart item quantity.
	 * @returns
	 */
    private handleUpdateItem(item: CartItem, quantity: number | undefined) {
        if (!quantity) { return; }
        cart.changeItem(item, quantity, null).then(() => {
            this.updateCartData();
        }).catch((event: unknown) => {
            if (event instanceof Error) {
                this.$log.error(event.message);
            }
        });
    }

    /**
	 * Update cart data.
	 */
    private updateCartData() {
        this.updateCartCount();
        this.updateTotalPrice();
        this.updatePrices();
        this.checkQuantities();
        this.handleContentDisplay();
    }

    /**
	 * Update cart count
	 * @returns
	 */
    private updateCartCount() {
        this.itemCount = cart.items.filter((item) => ~~item.quantity > 0)
            .map((obj) => ~~obj.quantity)
            .reduce((a, b) => a + b, 0);

        this._onCartItemCountChange.dispatch(this.itemCount);

        if (!this.count) { return; }

        for (const count of this.count) {
            count.innerHTML = String(this.itemCount);
        }
    }

    /**
	 * Update total price.
	 * @returns
	 */
    private updateTotalPrice() {
        if (!this.totalPrice) { return; }
        this.totalPrice.innerHTML = formatMoney(cart.totalPrice) as string;
    }

    private updatePrices() {
        if (!this.lineTotal) { return; }
        for (const line of this.lineTotal) {
            const id = line.dataset.idTotal;
            const match = cart.items.find((item) => item.id.toString() === id?.toString());
            if (line && match) {
                line.textContent = formatMoney(match.linePrice) as string;
            }
        }
    }
    private checkQuantities() {
        if (!this.quantitySelector) { return; }
        for (const quantity of this.quantitySelector) {
            const parent = quantity.parentNode as HTMLElement;
            if (!parent) { return; }

            const id = parent.dataset.id;
            const match = cart.items.find((item) => item.id.toString() === id?.toString());
            if (quantity && match) {
                quantity.updateQuantity(match.quantity);
            }
        }
    }
}
